import { useEffect, useMemo, useState } from "react";
import type { KeyboardEvent, ReactNode } from "react";
import { v4 } from "uuid";
import Box from "@mui/material/Box";
import type { SxProps, Theme } from "@mui/material/styles";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import type { TextFieldProps } from "@mui/material/TextField";
import { useFormikContext } from "formik";
import { get } from "lodash";

import { useFormContext } from "@/hooks/useFormContext";
import { FormSkeleton } from "../../skeleton";
import { InputBox } from "./input-box";

type FormValues = Record<string, unknown>;

export interface CodeVerificationProps {
  name?: string;
  length: number;
  readonly?: boolean;
  loading?: boolean;
  visible?: boolean;
  value?: string;
  variant?: TextFieldProps["variant"];
  onChange?: (value: string) => void;
  helperText?: SxProps<Theme>;
  onComplete?: () => void;
  error?: string;
}

export function CodeVerification({
  name,
  length,
  readonly,
  loading,
  visible = true,
  value,
  variant,
  onChange,
  helperText,
  onComplete = () => {},
  error,
}: CodeVerificationProps) {
  const [focused, setFocused] = useState(-1);
  const formContext = useFormContext();
  const formik = useFormikContext<FormValues>();
  const fieldName = name as string;
  const formikError = get(formik.touched, fieldName) && Boolean(get(formik.errors, fieldName));

  useEffect(() => {
    formik.setFieldError(fieldName, error);
  }, [error]);

  // out textfield
  const handleBlur = () => {
    setFocused(-1);
  };

  const onKeyDown = ({ event, index }: { event: KeyboardEvent<HTMLDivElement>; index: number }) => {
    if (event.key === "ArrowRight") {
      const array = splitString();
      if (array[index + 1] != null) setFocused(Math.min(index + 1, length - 1));
    }

    if (event.key === "ArrowLeft") {
      setFocused(Math.max(index - 1, 0));
    }

    if (event.key === "Backspace") {
      event.preventDefault();

      const array = splitString();
      array[index] = "";

      if (value !== undefined) {
        (onChange as (nextValue: string) => void)(array.join(""));
      } else {
        formik.setFieldValue(fieldName, array.join(""));
      }

      if (index === 0) {
        setFocused(-1);
      } else {
        setFocused(index - 1);
      }
    }
  };

  const onMouseDown = (index: number) => {
    const array = splitString();

    if (array[index - 1] == null && index !== 0) {
      setFocused(-1);
    }
  };

  function splitString() {
    const str =
      value ?? (get(formik.values, fieldName, Array(length).fill("")) as string | string[]);
    const tempArray = Array.from(str);
    const array = Array(length)
      .fill("")
      .map((_, index) => tempArray[index] || "");

    return array;
  }

  const setValue = (index: number, val: string | undefined) => {
    if (readonly) return;

    let vals = splitString();
    vals[index] = val || "";
    vals = vals.map((v, i) => (i > index ? "" : v));

    if (value !== undefined) {
      (onChange as (nextValue: string) => void)(vals.join(""));
    } else {
      formik.setFieldValue(fieldName, vals.join(""));
    }

    if (vals.some((v) => v === "")) {
      setFocused(index + 1);
    } else {
      setFocused(-1);
      onComplete();
    }
  };

  const values = useMemo(() => {
    return splitString().map((val, index) => (
      <InputBox
        type={visible ? "text" : "password"}
        key={v4()}
        index={index}
        isFocused={focused === index}
        value={val || ""}
        setValue={setValue}
        onKeyDown={(event) => onKeyDown({ event, index })}
        handleBlur={handleBlur}
        onMouseDown={() => onMouseDown(index)}
        variant={variant}
        error={formikError}
      />
    ));
  }, [focused, formikError, visible]);

  return (
    <FormSkeleton loading={loading ?? formContext?.loading}>
      <Box>
        <FormControl sx={{ display: "flex", flexDirection: "row", gap: "4px", mb: "10px" }}>
          {values}
        </FormControl>
        {formikError && (
          <FormHelperText
            sx={{
              width: "100%",
              px: 3,
              ...helperText,
            }}
          >
            {get(formik.errors, fieldName) as ReactNode}
          </FormHelperText>
        )}
      </Box>
    </FormSkeleton>
  );
}
